import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ActivityDetailDto } from '@/services/platform/api/model';
import { useClientTranslation, useLocaleStore } from '@/lib/core';
import { useApp } from '@/lib/core/providers/appProvider';
import { LinkRouteUrl } from '@/lib/core/utils';
import { Button } from '@/components/ui/button';
import { SpinLoading } from '@/components/shared/loadings/spin';
import { getActivityById } from '@/services/platform/data/activity';
import { ActivityOrder } from './order';
import { ActivityReservation } from './reservation';

export function ProfileActivityDetail() {
  const { pathname, state } = useLocation();
  const navigate = useNavigate();
  const locale = useLocaleStore(state => state.locale);
  const t = useClientTranslation();
  const { appSettings } = useApp();

  // Handoff rule: the homepage CTA only shows when arriving right after a
  // purchase (order-confirmed → track order), never via Profile > Activity.
  const fromPurchase = Boolean((state as { fromPurchase?: boolean } | null)?.fromPurchase);

  const id = pathname.split('/').filter(Boolean).pop() ?? '';

  const [activity, setActivity] = useState<ActivityDetailDto | undefined>(undefined);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) {
      setLoading(false);
      return;
    }
    getActivityById(id, locale)
      .then(data => setActivity(data))
      .finally(() => setLoading(false));
  }, [id, locale]);

  if (loading) return <SpinLoading wrapperClassName="min-h-[30vh]" />;

  if (!activity) {
    return (
      <div className="flex flex-1 items-center justify-center px-4 pt-22">
        <p className="font-pp-neue-montreal text-jncquoi-neutral-700 text-center text-[14px] tracking-[0.5px]">
          {t('App.Labels.SomethingWentWrong')}
        </p>
      </div>
    );
  }

  const goHomepage = () => {
    const homeHref = LinkRouteUrl(appSettings?.properties?.shop?.route);
    navigate(homeHref ?? '/', { replace: true });
  };

  return (
    <div className="flex flex-1 flex-col overflow-y-auto px-4 pt-6">
      {activity.kind === 'order' && activity.order ? (
        <ActivityOrder order={activity.order} name={activity.name} />
      ) : activity.reservation ? (
        <ActivityReservation reservation={activity.reservation} name={activity.name} />
      ) : null}

      {fromPurchase && (
        <div className="bg-jncquoi-neutral-00 border-jncquoi-neutral-300 fixed right-0 bottom-0 left-0 z-30 border-t-[0.5px] px-4 pt-4 pb-10.5">
          <Button variant="primary-solid" palette="dark" size="md" className="w-full" onClick={goHomepage}>
            {t('App.Shop.GoToHomepage')}
          </Button>
        </div>
      )}
    </div>
  );
}
